import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import HelpPdfModal from "./HelpPdfModal";
import "./EmployeeNotice.css";

// 管理側のお知らせ作成（category="company"）のみ表示する。
// イベント(event/event_cancelled)・勤怠/システム自動通知は除外する。
const SYSTEM_AUTO_NOTICE_TITLES = new Set([
  "申請を受け付けました",
  "事前申請を受け付けました",
  "出勤を記録しました",
  "退勤を記録しました",
  "イベントのお知らせ",
  "スケジュール登録のお願い",
  "打刻のお願い",
  "【申請承認のお知らせ】",
  "【申請却下のお知らせ】",
  "【申請差し戻しのお知らせ】",
  "【上司承認のお知らせ】残業の本申請をお願いします",
  "【上司却下のお知らせ】",
]);

const isCompanyNotice = (n) =>
  n && n.category === "company" && !SYSTEM_AUTO_NOTICE_TITLES.has(n.title);

const isPdf = (att) =>
  (att?.mime_type || "").toLowerCase().includes("pdf") ||
  (att?.filename || "").toLowerCase().endsWith(".pdf");

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const EmployeeNotice = () => {
  const navigate = useNavigate();
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [pdfView, setPdfView] = useState(null); // { title, url }

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/notifications/", { params: { _t: Date.now() } });
      const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setNotices(list.filter(isCompanyNotice));
    } catch (e) {
      console.error(e);
      setMessage("お知らせの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleMarkRead = async (id) => {
    try {
      await api.post(`/api/notifications/${id}/mark-read/`);
      setNotices((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch (e) {
      console.error(e);
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notices.filter((n) => !n.is_read);
    await Promise.all(
      unread.map((n) => api.post(`/api/notifications/${n.id}/mark-read/`).catch(() => {}))
    );
    setNotices((prev) => prev.map((n) => ({ ...n, is_read: true })));
  };

  const formatDate = (dt) => {
    if (!dt) return "";
    return new Date(dt).toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const openAttachment = (att) => {
    if (!att?.url) return;
    if (isPdf(att)) {
      setPdfView({ title: att.filename || "PDF", url: att.url });
    } else {
      window.open(att.url, "_blank", "noopener,noreferrer");
    }
  };

  const unreadCount = notices.filter((n) => !n.is_read).length;

  return (
    <div className="en-container">
      <div className="en-header">
        <button type="button" className="en-back" onClick={() => navigate(-1)} aria-label="戻る">
          ←
        </button>
        <h1 className="en-title">お知らせ</h1>
        {unreadCount > 0 && (
          <button type="button" className="en-readall" onClick={handleMarkAllRead}>
            全て既読
          </button>
        )}
      </div>

      {message && <p className="en-error">{message}</p>}

      {loading ? (
        <p className="en-empty">読み込み中...</p>
      ) : notices.length === 0 ? (
        <p className="en-empty">お知らせはありません。</p>
      ) : (
        <div className="en-list">
          {notices.map((n) => {
            const attachments = Array.isArray(n.attachments) ? n.attachments : [];
            return (
              <div key={n.id} className={`en-card${n.is_read ? "" : " en-card--unread"}`}>
                <div className="en-card-head">
                  {!n.is_read && <span className="en-dot" aria-label="未読" />}
                  <span className="en-card-title">{n.title}</span>
                  <span className="en-card-date">{formatDate(n.created_at)}</span>
                </div>

                {(n.content || n.message) && (
                  <div className="en-card-body">{n.content || n.message}</div>
                )}

                {attachments.length > 0 && (
                  <div className="en-attachments">
                    {attachments.map((att) => (
                      <button
                        key={att.id}
                        type="button"
                        className="en-attachment"
                        onClick={() => openAttachment(att)}
                        title={att.filename}
                      >
                        <span className="en-att-icon">{isPdf(att) ? "📄" : "📎"}</span>
                        <span className="en-att-name">{att.filename}</span>
                        {att.file_size ? (
                          <span className="en-att-size">{formatSize(att.file_size)}</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                )}

                {!n.is_read && (
                  <div className="en-card-actions">
                    <button type="button" className="en-read-btn" onClick={() => handleMarkRead(n.id)}>
                      既読にする
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pdfView && (
        <HelpPdfModal
          title={pdfView.title}
          url={pdfView.url}
          onClose={() => setPdfView(null)}
        />
      )}
    </div>
  );
};

export default EmployeeNotice;
